<template>
  <CalicoOnboarding
    type="financials"
    :form-data="formData"
    :loading="loading"
    @save="save"
  >
    <div class="financials">
      <div class="section">
        <div class="header">
          <h1 class="heading--xsmall">
            Income Statement
          </h1>
          <p class="body--medium">
            Enter the following information found on your most recent income statement.
          </p>
        </div>
        <CalicoForm
          v-slot="{ field, error }"
          name="revenue"
          label="Revenue"
          :tooltip="{ header: Tooltips.FINANCIALS_REVENUE_HEADER, body: Tooltips.FINANCIALS_REVENUE_BODY }"
        >
          <CalicoField 
            v-model="formData.revenue"
            v-bind="field"
            :error="error"
            :only-numbers="true"
            :symbol="'$'"
            :custom-format="formatAndRoundCurrency"
          />
        </CalicoForm>
        <CalicoForm
          v-slot="{ field, error }"
          name="costOfGoodsSold"
          label="Cost of Revenue or Cost of Goods Sold"
          :tooltip="{ header: Tooltips.FINANCIALS_COST_OF_REVENUE_HEADER, body: Tooltips.FINANCIALS_COST_OF_REVENUE_BODY }"
        >
          <CalicoField 
            v-model="formData.costOfGoodsSold"
            v-bind="field"
            :error="error"
            :only-numbers="true"
            :symbol="'$'"
            :custom-format="formatAndRoundCurrency"
          />
        </CalicoForm>
        <CalicoForm
          v-slot="{ field, error }"
          name="operatingIncome"
          label="Operating Income"
          :tooltip="{ header: Tooltips.FINANCIALS_OPERATING_INCOME_HEADER, body: Tooltips.FINANCIALS_OPERATING_INCOME_BODY }"
        >
          <CalicoField 
            v-model="formData.operatingIncome"
            v-bind="field"
            :error="error"
            :only-numbers="true"
            :symbol="'$'"
            :custom-format="formatAndRoundCurrency"
          />
        </CalicoForm>
        <CalicoForm
          v-slot="{ field, error }"
          name="netIncome"
          label="Net Income"
          :tooltip="{ header: Tooltips.FINANCIALS_NET_INCOME_HEADER, body: Tooltips.FINANCIALS_NET_INCOME_BODY }"
        >
          <CalicoField 
            v-model="formData.netIncome"
            v-bind="field"
            :error="error"
            :only-numbers="true"
            :symbol="'$'"
            :custom-format="formatAndRoundCurrency"
          />
        </CalicoForm>
      </div>
      <div class="section">
        <div class="header">
          <h1 class="heading--xsmall">
            Balance Sheet
          </h1>
          <p class="body--medium">
            Enter the following information found on your most recent balance sheet.
          </p>
        </div>
        <CalicoForm
          v-slot="{ field, error }"
          name="currentAssets"
          label="Current Assets"
          :tooltip="{ header: Tooltips.FINANCIALS_CURRENT_ASSETS_HEADER, body: Tooltips.FINANCIALS_CURRENT_ASSETS_BODY }"
        >
          <CalicoField 
            v-model="formData.currentAssets"
            v-bind="field"
            :error="error"
            :only-numbers="true"
            :symbol="'$'"
            :custom-format="formatAndRoundCurrency"
          />
        </CalicoForm>
        <CalicoForm
          v-slot="{ field, error }"
          name="totalAssets"
          label="Total Assets"
          :tooltip="{ header: Tooltips.FINANCIALS_TOTAL_ASSETS_HEADER, body: Tooltips.FINANCIALS_TOTAL_ASSETS_BODY }"
        >
          <CalicoField 
            v-model="formData.totalAssets"
            v-bind="field"
            :error="error"
            :only-numbers="true"
            :symbol="'$'"
            :custom-format="formatAndRoundCurrency"
          />
        </CalicoForm>
        <CalicoForm
          v-slot="{ field, error }"
          name="otherAssets"
          label="Other Assets"
          :tooltip="{ header: Tooltips.FINANCIALS_OTHER_ASSETS_HEADER, body: Tooltips.FINANCIALS_OTHER_ASSETS_BODY }"
        >
          <CalicoField 
            v-model="formData.otherAssets"
            v-bind="field"
            :error="error"
            :only-numbers="true"
            :symbol="'$'"
            :custom-format="formatAndRoundCurrency"
          />
        </CalicoForm>
        <CalicoForm
          v-slot="{ field, error }"
          name="currentLiabilities"
          label="Current Liabilities"
          :tooltip="{ header: Tooltips.FINANCIALS_CURRENT_LIABILITIES_HEADER, body: Tooltips.FINANCIALS_CURRENT_LIABILITIES_BODY }"
        >
          <CalicoField 
            v-model="formData.currentLiabilities"
            v-bind="field"
            :error="error"
            :only-numbers="true"
            :symbol="'$'"
            :custom-format="formatAndRoundCurrency"
          />
        </CalicoForm>
        <CalicoForm
          v-slot="{ field, error }"
          name="totalLiabilities"
          label="Total Liabilities"
          :tooltip="{ header: Tooltips.FINANCIALS_TOTAL_LIABILITIES_HEADER, body: Tooltips.FINANCIALS_TOTAL_LIABILITIES_BODY }"
        >
          <CalicoField 
            v-model="formData.totalLiabilities"
            v-bind="field"
            :error="error"
            :only-numbers="true"
            :symbol="'$'"
            :custom-format="formatAndRoundCurrency"
          />
        </CalicoForm>
      </div>
      <div class="section">
        <div class="header">
          <h1 class="heading--xsmall">
            Personal Balance Sheet
          </h1>
          <p class="body--medium">
            Enter the following information found on your most recent personal balance sheet.
          </p>
        </div>
        <CalicoForm
          v-slot="{ field, error }"
          name="totalPersonalAssets"
          label="Total Personal Assets"
          :tooltip="{ header: Tooltips.FINANCIALS_TOTAL_PERSONAL_ASSETS_HEADER, body: Tooltips.FINANCIALS_TOTAL_PERSONAL_ASSETS_BODY }"
        >
          <CalicoField 
            v-model="formData.totalPersonalAssets"
            v-bind="field"
            :error="error"
            :only-numbers="true"
            :symbol="'$'"
            :custom-format="formatAndRoundCurrency"
          />
        </CalicoForm>
        <CalicoForm
          v-slot="{ field, error }"
          name="totalPersonalLiabilities"
          label="Total Personal Liabilities"
          :tooltip="{ header: Tooltips.FINANCIALS_TOTAL_PERSONAL_LIABILITIES_HEADER, body: Tooltips.FINANCIALS_TOTAL_PERSONAL_LIABILITIES_BODY }"
        >
          <CalicoField 
            v-model="formData.totalPersonalLiabilities"
            v-bind="field"
            :error="error"
            :only-numbers="true"
            :symbol="'$'"
            :custom-format="formatAndRoundCurrency"
          />
        </CalicoForm>
      </div>
      <div class="section">
        <div class="header">
          <h1 class="heading--xsmall">
            Loan Information
          </h1>
          <p class="body--medium">
            Enter the loan amount you’re seeking. If you are not looking for a loan, enter ‘0’
          </p>
        </div>
        <CalicoForm
          v-slot="{ field, error }"
          name="loanAmount"
          label="Loan Amount"
        >
          <CalicoField 
            v-model="formData.loanAmount"
            v-bind="field"
            :error="error"
            :only-numbers="true"
            :symbol="'$'"
            :custom-format="formatAndRoundCurrency"
          />
        </CalicoForm>
      </div>
    </div>
  </CalicoOnboarding>
</template>

<script>
import CalicoOnboarding from "@/components/template/onboarding/index.vue";
import CalicoForm from "@/components/pattern/form/index.vue";
import CalicoField from '@/components/calico/field/index.vue';
import services from "@/services";
import { Tooltips } from "@/enums/index.js";

export default {
    name: "BusinessFinancials",
    components: {
        CalicoOnboarding,
        CalicoForm,
        CalicoField
    },
    data() {
        return {
            formData: {
                revenue: '',
                costOfGoodsSold: '',
                netIncome: '',
                operatingIncome: '',
                currentAssets: '',
                currentLiabilities: '',
                totalLiabilities: '',
                totalAssets: '',
                otherAssets: '',
                totalPersonalLiabilities: '',
                totalPersonalAssets: '',
                loanAmount: ''
            },
            financials: {},
            loading: true,
            businessId: 0,
            Tooltips
        }
    },

    async mounted() {
        const business = await services.user.business.get();
        this.businessId = business.id
        this.formData = await services.user.financials.get(this.businessId);
        this.loading = false
    },

    methods: {
        async save(next) {
            if (this.formData.otherAssets === '') {
                this.formData.otherAssets = 0;
            }
            this.loading = true            
            await services.financials.calculate(this.businessId, {
                revenue: this.formData.revenue || 0,
                costOfGoodsSold: this.formData.costOfGoodsSold || 0,
                netIncome: this.formData.netIncome || 0,
                operatingIncome: this.formData.operatingIncome || 0,
                currentAssets: this.formData.currentAssets || 0,
                currentLiabilities: this.formData.currentLiabilities || 0,
                totalLiabilities: this.formData.totalLiabilities || 0,
                totalAssets: this.formData.totalAssets || 0,
                otherAssets: this.formData.otherAssets || 0,
                totalPersonalLiabilities: this.formData.totalPersonalLiabilities || 0,
                totalPersonalAssets: this.formData.totalPersonalAssets || 0,
                loanAmount: this.formData.loanAmount || 0
            })   
            next()
            this.loading = false
        },

        formatAndRoundCurrency(value) {
          const numeric = parseFloat(value);
          const rounded = isNaN(numeric) ? 0 : Math.round(numeric);
          return {
            raw: rounded,
            formatted: rounded.toLocaleString('en-US')
          };
        }   
    }
}
</script>

<style scoped lang="scss">
.financials {
    @include inset-space($space-large);
    @include stack-space($space-xlarge);
    .section {
        @include stack-space($space-large);
        .header {
            @include stack-space($space-small);
            p {
                margin: 0
            }
        }
    }
}
</style>